import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  isLanguage,
  resolveLanguage,
} from '@dorixona/i18n'
import type { Language } from '@dorixona/i18n'
import { useApi } from '../api/context'
import { useSession } from './session'
import { setNativeLanguage, whenBridgeReady } from './nativeBridge'

/**
 * Язык интерфейса и его смена. Выбор запоминается в браузере (он главнее
 * Telegram и системы при следующем входе), данные перезапрашиваются — сервер
 * отдаёт названия и тексты на новом языке, — а у вошедшего покупателя язык
 * сохраняется и в профиле: на нём придут СМС и уведомления. В нашем
 * Flutter-приложении выбор уходит и оболочке — на нём её экран PIN-кода.
 */
/** Выбирал ли покупатель язык сам: нет — при первом заходе спросим. */
export function hasChosenLanguage(): boolean {
  try {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY) !== null
  } catch {
    // Хранилище закрыто (приватный режим) — не мучаем окном на каждом заходе.
    return true
  }
}

function rememberLanguage(language: Language) {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
  } catch {
    // Не запомнилось — язык всё равно переключится на эту сессию.
  }
}

export function useLanguage() {
  const { i18n } = useTranslation()
  const queryClient = useQueryClient()
  const api = useApi()
  const { customer } = useSession()
  const language = resolveLanguage([i18n.language])

  const change = (next: Language) => {
    // Выбор запоминаем всегда — и тот же язык тоже: по нему приветствие
    // понимает, что язык уже выбирали.
    rememberLanguage(next)
    void setNativeLanguage(next)
    if (next === language) return
    void i18n.changeLanguage(next).then(() => queryClient.invalidateQueries())
    if (customer && customer.gender) {
      // Не вышло — не беда: интерфейс уже переключён, профиль догонит при
      // следующем сохранении данных.
      void api
        .patch('/me', {
          first_name: customer.firstName,
          last_name: customer.lastName,
          gender: customer.gender,
          language: next,
        })
        .catch(() => undefined)
    }
  }

  return { language, languages: SUPPORTED_LANGUAGES, change }
}

/**
 * При запуске в Flutter-приложении отдаём оболочке язык, который покупатель
 * уже выбирал. Не выбирал — молчим: выбор он сделает в приветствии, и тогда
 * язык уйдёт оболочке вместе с ним.
 */
export async function syncNativeLanguage(storage: Pick<Storage, 'getItem'> = localStorage) {
  if (!(await whenBridgeReady())) return
  let saved: string | null = null
  try {
    saved = storage.getItem(LANGUAGE_STORAGE_KEY)
  } catch {
    return
  }
  if (isLanguage(saved)) await setNativeLanguage(saved)
}
